import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import ClipLoader from "react-spinners/ClipLoader";

import { ReactComponent as ArrowIcon } from "@content/img/shared/home/svg/arrow-icon.svg";
import defaultLogo from "@content/img/shared/education/png/default-logo.png";

import { loadHollandResults } from "../../helpers/hollandStorage";
import { getHollandTypeInfo } from "../../helpers/hollandScores";
import {
  buildCareerSearchText,
  fetchCareerGuidanceByHolland,
  fetchCompaniesWithFacultyByCodes,
} from "../../helpers/hollandApi";

import styles from "./styles/hollandEducation.module.css";

function getSpecialities(company) {
  return (company.Faculty || []).slice(0, 4);
}

export default function HollandEducationResults({ hollandData: hollandDataProp }) {
  const [hollandData, setHollandData] = useState(
    () => hollandDataProp || loadHollandResults()
  );
  const [careerItems, setCareerItems] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [load, setLoad] = useState(false);

  useEffect(() => {
    setHollandData(hollandDataProp || loadHollandResults());
  }, [hollandDataProp]);

  const loadResults = useCallback(async (data) => {
    if (!data?.hollandApi) {
      setCareerItems([]);
      setCompanies([]);
      return;
    }

    setLoad(true);

    try {
      const careerRes = await fetchCareerGuidanceByHolland(data.hollandApi, {
        limit: 30,
        offset: 0,
        type: 1,
      });
      const items = careerRes.data || [];
      setCareerItems(items);

      const searchText = buildCareerSearchText(items, 6);
      const oksoCodes = [
        ...new Set(items.map((item) => item.OksoCode).filter(Boolean)),
      ].slice(0, 8);

      let facultyRes = { Data: [] };

      if (oksoCodes.length > 0) {
        facultyRes = await fetchCompaniesWithFacultyByCodes({
          text: "",
          faculty: oksoCodes,
          limit: 10,
          offset: 0,
        });
      }

      if (!facultyRes.Data?.length && searchText) {
        facultyRes = await fetchCompaniesWithFacultyByCodes({
          text: searchText,
          limit: 10,
          offset: 0,
        });
      }

      setCompanies(facultyRes.Data || []);
    } catch {
      setCareerItems([]);
      setCompanies([]);
    } finally {
      setLoad(false);
    }
  }, []);

  useEffect(() => {
    loadResults(hollandData);
  }, [hollandData, loadResults]);

  if (!hollandData) {
    return (
      <div className={styles.empty}>
        <p>Сначала пройдите тест Холланда на первом этапе.</p>
      </div>
    );
  }

  return (
    <div className={styles.block}> 
      <div className={styles.headerRow}>
        <div>
          <span className={styles.kicker}>По результатам теста</span>
          <h4 className={styles.title}>
            Специальности и учебные заведения
          </h4>
          <p className={styles.subtitle}>
            Код Холланда: <strong>{hollandData.hollandCode}</strong>
          </p>
        </div>
        <Link to="/fschool/holland-results" className={styles.summaryLink}>
          Полная сводка
        </Link>
      </div>

      {load ? (
        <div className={styles.loader}>
          <ClipLoader />
        </div>
      ) : (
        <>
          <section className={styles.section}>
            <h5 className={styles.sectionTitle}>
              Подходящие специальности и профессии
            </h5>
            {careerItems.length > 0 ? (
              <div className={styles.careerList}>
                {careerItems.map((item) => (
                  <article key={item.Id} className={styles.careerCard}>
                    <div className={styles.careerMatch}>
                      Совпадение: {Math.round((item.MatchScore || 0) * 100)}%
                    </div>
                    <div className={styles.careerOkso}>
                      {item.OksoCode ? `${item.OksoCode} — ` : ""}
                      {item.OksoTitle || "—"}
                    </div>
                    <div className={styles.careerOkz}>
                      {item.OkzTitle || item.SpecialistNeedsItem || "—"}
                    </div>
                  </article>
                ))}
              </div>
            ) : (
              <p className={styles.emptyText}>Подходящие специальности не найдены.</p>
            )}
          </section>

          <section className={styles.section}>
            <h5 className={styles.sectionTitle}>Учебные заведения</h5>
            {companies.length > 0 ? (
              <div className={styles.results}>
                {companies.map((item) => (
                  <div key={item.Id} className={styles.result}>
                    <div className={styles.card}>
                      <div className={styles.city}>{item.Address}</div>
                      <div className={styles.info}>
                        <div className={styles.about}>
                          <img src={defaultLogo} alt="Результат" />
                          <div className={styles.companyTitle}>
                            {item.Shortname ? item.Shortname : item.Fullname}
                          </div>
                        </div>
                        <Link
                          to={`/obrazovanie-v-bashkortostane/${
                            item.IdType === 2 ? "vuzy-rb" : "kolledzhi-rb"
                          }/${item.Slug}`}
                          className={styles.toCompany}
                          target="_blank"
                        >
                          Перейти к учебному заведению
                          <ArrowIcon />
                        </Link>
                      </div>
                      <div className={styles.specialities}>
                        {item.Faculty?.length > 0 ? (
                          <>
                            <div className={styles.specialitiesTitle}>
                              <span>
                                {item.Faculty[0].Name || item.Faculty[0].Title}
                              </span>
                              {item.Faculty.length > 1 ? (
                                <>
                                  <span>и</span>
                                  <span className={styles.colored}>
                                    еще {item.Faculty.length - 1} специальностей
                                  </span>
                                </>
                              ) : null}
                            </div>
                            {getSpecialities(item).map((faculty) => (
                              <div key={faculty.Id} className={styles.speciality}>
                                <div className={styles.specialityName}>
                                  {faculty.Name || faculty.Title}
                                </div>
                              </div>
                            ))}
                          </>
                        ) : (
                          "Нет специальностей"
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className={styles.emptyText}>
                Учебные заведения по выбранным специальностям не найдены.
              </p>
            )}
          </section>

          <div className={styles.typeHints}>
            {hollandData.sortedTypes?.slice(0, 3).map((type) => {
              const info = getHollandTypeInfo(type);
              if (!info) return null;
              return (
                <div key={type} className={styles.typeHint}>
                  <strong>
                    {type} — {info.title}
                  </strong>
                  <p>{info.description}</p>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
